import LaunchIcon from '@mui/icons-material/Launch';
import { channelId } from '@/utils';
import StatusChip from '@spa/components/StatusChip';
import useProgressiveList, {
  DEFAULT_BATCH_SIZE,
} from '@spa/hooks/useProgressiveList';
import { useLang } from '@spa/i18n/useLang';
import type { Company } from '@spa/types/companies.types';
import type { RefObject } from 'react';

const selectBtnCls =
  'px-4 py-1.5 border rounded text-sm font-medium text-primary-text! border-brand-text hover:bg-brand-text! hover:text-white! transition-colors disabled:opacity-50';

const deselectBtnCls =
  'px-4 py-1.5 border rounded text-sm font-medium text-muted-foreground border-biomar-border hover:bg-brand-text! hover:text-white transition-colors disabled:opacity-50';

interface CompanyPickerListContentProps {
  companies: Company[];
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  ownCompanyId?: string | number;
  activeCompanyId?: string | number;
  onSelectCompany: (company: Company) => void;
  onDeselectCompany: () => void;
}

function CompanyPickerListContent({
  companies,
  scrollContainerRef,
  isOpen,
  isLoading,
  isSuperAdmin,
  ownCompanyId,
  activeCompanyId,
  onSelectCompany,
  onDeselectCompany,
}: CompanyPickerListContentProps) {
  const t = useLang();
  const { visibleItems, hasMore } = useProgressiveList(companies, {
    scrollContainerRef,
    isOpen,
  });

  return (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0">
      <div className="border border-biomar-border bg-white rounded shadow-dropdown">
        {companies.length === 0 ? (
          <p className="py-8 text-center">
            {t('companyPicker.search.noResults')}
          </p>
        ) : (
          <>
            {visibleItems.map((entry, index) => {
              const staggerIndex = index % DEFAULT_BATCH_SIZE;

              const isActiveMasquerade =
                Number(entry.company.companyId) === Number(activeCompanyId);

              // Company User
              const isOwnCompany =
                !isSuperAdmin &&
                Number(entry.company.companyId) === Number(ownCompanyId);

              const isActiveChannel = entry.channels.some(
                (channel) => channel.channelId === channelId
              );

              const externalChannel =
                !isActiveChannel &&
                entry.channels.find(
                  (channel) => channel.channelId !== channelId
                );

              return (
                <div
                  key={entry.company.companyId}
                  style={{ animationDelay: `${staggerIndex * 30}ms` }}
                  className={`
                    animate-fadeSlideUp opacity-0
                    flex flex-col sm:flex-row sm:items-center sm:justify-between
                    px-4 py-4
                    border-b border-biomar-border
                    hover:bg-modal-background-hover transition-colors
                    ${isActiveMasquerade ? 'bg-modal-background-hover' : ''}
                  `}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                    <span className="text-sm break-words">
                      {entry.company.companyName}
                    </span>

                    {isSuperAdmin && isActiveMasquerade && (
                      <StatusChip
                        label={t('companyPicker.superAdmin.selected')}
                      />
                    )}

                    {isOwnCompany &&
                      (!activeCompanyId || isActiveMasquerade) && (
                        <StatusChip
                          label={t('companyPicker.companyUser.yourCompany')}
                        />
                      )}

                    {!isSuperAdmin && isActiveMasquerade && !isOwnCompany && (
                      <StatusChip
                        label={t('companyPicker.companyUser.representative')}
                      />
                    )}
                  </div>

                  {(isSuperAdmin || !isActiveMasquerade || !isOwnCompany) && (
                    <div className="mt-2 flex justify-end sm:mt-0 sm:ml-4 sm:shrink-0">
                      {isActiveMasquerade ? (
                        <button
                          onClick={onDeselectCompany}
                          disabled={isLoading}
                          className={deselectBtnCls}
                        >
                          {isSuperAdmin
                            ? t('companyPicker.superAdmin.endMasqueradeAction')
                            : t('companyPicker.companyUser.deselectAction')}
                        </button>
                      ) : externalChannel ? (
                        <button
                          onClick={() =>
                            window.open(externalChannel.site, '_blank')?.focus()
                          }
                          className={`${selectBtnCls} flex items-center gap-1`}
                        >
                          {externalChannel.channelName}
                          <LaunchIcon sx={{ fontSize: 14 }} />
                        </button>
                      ) : (
                        <button
                          onClick={() => onSelectCompany(entry)}
                          disabled={isLoading}
                          className={selectBtnCls}
                        >
                          {isSuperAdmin
                            ? t('companyPicker.superAdmin.masqueradeAction')
                            : t('companyPicker.companyUser.selectAction')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {hasMore && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {t('companyPicker.loading')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CompanyPickerListContent;
